import Dialog from "@mui/material/Dialog";
import { ColDef, GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import AlertDialog from "../../components/shared/AlertDialog";
import {
  Box,
  ButtonPrimary,
  HStack,
  isCompanySettingsReadOnly,
  VStack,
} from "../../components/utils";
import { formatDisplayDate } from "../../utils/date";
import AddOrEditBlackOutDate from "./AddOrEditBlackOutDate";
import {
  useDeleteBlackOutDay,
  useGetBlackOutDays,
} from "../../queries/transactionV2";
import { CTADropdown } from "../../components/shared/Dropdown";

function BlackOutDates() {
  const isUserReadOnly = isCompanySettingsReadOnly();
  const gridApi = useRef<GridApi | null>(null);
  const { data: _blackOutDates } = useGetBlackOutDays();

  const [dialog, setDialog] = useState<{
    open: boolean;
    mode?: "Add" | "Edit";
    data?: any;
  }>({
    open: false,
    mode: "Add",
    data: undefined,
  });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    data?: any;
  }>({
    open: false,
    data: undefined,
  });

  const blackOutDatesData = _blackOutDates?.sort(
    (a: any, b: any) =>
      new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: [],
      },
      menuTabs: [],
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );

  useEffect(() => {
    if (blackOutDatesData !== undefined) {
      setFilteredRowData(blackOutDatesData);
    }
  }, [blackOutDatesData]);

  // CTA Dropdown actions for each row
  function handleAction(
    blackout: any,
    action: { name: string; disabled?: boolean }
  ) {
    if (action.name === "Edit Blackout Date" && !action.disabled) {
      setDialog({ open: true, mode: "Edit", data: blackout });
    } else if (action.name === "Delete Blackout Date" && !action.disabled) {
      setDeleteDialog({ open: true, data: blackout });
    }
  }

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "START DATE",
        field: "startDate",
        valueFormatter: (params) => formatDisplayDate(params.value),
        sortable: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "END DATE",
        field: "endDate",
        valueFormatter: (params) => formatDisplayDate(params.value),
        sortable: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "REASON",
        field: "reason",
        sortable: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "NOTES",
        field: "notes",
        sortable: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "",
        field: "actions",
        pinned: "right",
        hide: false,
        width: 80,
        maxWidth: 80,
        filter: false,
        colId: "action-column",
        resizable: false,
        sortable: false,
        menuTabs: [],
        cellRenderer: CTADropdown,
        cellRendererParams: (params: any) => ({
          actions: [
            { name: "Edit Blackout Date", disabled: isUserReadOnly },
            { name: "Delete Blackout Date", disabled: isUserReadOnly },
          ],
          onAction: (action: any) => handleAction(params.data, action),
        }),
      },
    ],
    [isUserReadOnly]
  );

  const [filteredRowData, setFilteredRowData] =
    useState<any>(blackOutDatesData);

  const onGridReady = (params: any) => {
    gridApi.current = params;
  };

  const { mutate: deleteBlackOutDate } = useDeleteBlackOutDay();

  function deleteBlackOutDateEntry() {
    deleteBlackOutDate(deleteDialog.data?.id || "", {
      onSuccess: () => {
        toast("Blackout date deleted successfully!", {
          type: "success",
          autoClose: 2000,
        });
        setDeleteDialog({ open: false });
      },
      onError: (err: any) => {
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
        setDeleteDialog({ open: false });
      },
    });
  }

  return (
    <Box className="w-full p-4 bg-white rounded-lg ">
      <HStack className="items-center justify-between mb-8">
        <VStack className="min-w-max">
          <p className="text-lg font-medium text-gray-dark">Blackout Dates</p>
          <p className="text-xs font-medium text-gray-light">
            {filteredRowData?.length} Blackout Dates
          </p>
        </VStack>
        <ButtonPrimary
          disabled={isUserReadOnly}
          onClick={() => {
            setDialog({ open: true });
          }}
        >
          Add Blackout Date
        </ButtonPrimary>
        <Dialog open={dialog.open} maxWidth="md" fullWidth>
          <AddOrEditBlackOutDate
            onClose={() => setDialog({ open: false })}
            mode={dialog.mode || "Add"}
            data={dialog.data}
          />
        </Dialog>
      </HStack>

      <HStack className="justify-between w-full">
        <Box
          style={{
            height: "400px",
          }}
          className="w-full h-full max-h-full overflow-x-auto ag-theme-material"
        >
          <AgGridReact
            rowData={filteredRowData}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            defaultColDef={defaultColDef}
            suppressRowTransform={true}
            animateRows={true}
            pagination={true}
            rowClass={
              "border-t border-dashed cursor-pointer hover:bg-slate-50 "
            }
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
            }
          />
        </Box>
      </HStack>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false })}
        maxWidth="md"
      >
        <AlertDialog
          onClose={() => setDeleteDialog({ open: false })}
          open={deleteDialog.open}
          message={"Do you want to delete this blackout date?"}
          onPrimaryAction={deleteBlackOutDateEntry}
          onSecondaryAction={() => setDeleteDialog({ open: false })}
        />
      </Dialog>
    </Box>
  );
}

export default BlackOutDates;
